// 領収書発行ウィジェットの本体（Issue #46 第1段階）。
//
// おちゃのこ各店に11枚コピーされている「領収書発行ページ」（AngularJS 1.7 + res の ReceiptIssue.js）を
// 1つのウィジェットに寄せたもの。第1段階で UI を移し、第2段階（Issue #82）で通常版の API を
// wuStorefront の自前の口へ移した。別注版も Issue #97 で同じ形の自前の口へ移した（api.ts の先頭）。
//
// 通常版（receipt-issue / index.ts）と別注版（est-receipt-issue / est.ts）の 2 つの名前で登録され、
// どちらも createReceiptWidget(kind) が返す同じ実装で動く。違いは text.ts / api.ts / template.ts の
// kind 分岐だけ（API のパス・文言・発行後に出すのがメールの案内かダウンロードのリンクか）。
//
// 店側の書き方:
//   <div data-wu-widget="receipt-issue"
//        data-descriptions="商品代金,PP袋代,ブックカバー代,台紙代金,マスク代,商品代"
//        data-top-url="https://www.anshinya.com/"></div>
//   <div data-wu-widget="est-receipt-issue"
//        data-descriptions="商品代金,PP袋代,PPシート代,ブックカバー代,商品代"
//        data-top-url="https://www.opp-honpo.net/"></div>
//
// ★★URL は変えられない。
//   お客様の発送完了メールに https://www.anshinya.com/page/97?guid=... の形で入っており、
//   送信済みのメールは書き換えられない（2024年12月のメールから2026年9月に発行された実例がある）。
//   ⇒ 各店のフリーページは残し、URL も変えず、中身だけをこのタグに差し替える。
//   ⇒ guid は属性ではなく **URL のクエリ `?guid=`** から読む。
//
// ★data-site は要らない（この API は guid だけで引ける）。省略すると shared/site-id.ts が
//   ホスト名から SiteInfoId を引く。
//
// ★構造はウィジェット、装飾は各店（style.ts の先頭に理由）。
//   `wu-receipt-*` のクラス名は各店の CSS から参照される契約。勝手に変えない（template.ts の注記）。
//
// ★★発行ボタンを押すと、お客様へ本物の領収書メールが飛ぶ（別注版は発行済みフラグが立つ）。
//   表示だけ確かめたいときは ?wu_receipt_preview=（preview.ts）を使う。こちらは API を一切叩かない。

import { injectStyleOnce } from '../../shared/style'
import type { Widget, WidgetContext } from '../../shared/registry'
import { DEFAULT_API_BASE, fetchOrderInfo, issueReceipt, type OrderInfo } from './api'
import { parseDescriptions, parseTopUrl, readGuid } from './options'
import { PREVIEW_GUID, PREVIEW_LABEL, PREVIEW_ORDER, PREVIEW_ORDER_INFO, readPreview } from './preview'
import { CSS } from './style'
import {
  doneView,
  errorItemsHtml,
  errorView,
  issueView,
  notFoundView,
  reissueView,
  sendingView,
  type State,
  type ViewContext,
  type ViewModel,
} from './template'
import type { ReceiptKind } from './text'
import { canIssue, validateName } from './validate'

/** プレビューで「処理中」を見せる時間。実際の送信では応答が来たら即座に次へ進む。 */
const PREVIEW_SENDING_MS = 900

export function createReceiptWidget(kind: ReceiptKind): Widget {
  return (el: HTMLElement, _ctx: WidgetContext): void => mount(el, kind)
}

function mount(el: HTMLElement, kind: ReceiptKind): void {
  injectStyleOnce('receipt-issue', CSS)
  el.classList.add('wu-receipt')
  // 別注版だけ印を足す。店の CSS が両方に効くよう、他のクラス名は通常版と共通。
  if (kind === 'est') el.classList.add('wu-receipt-est')

  const apiBase = el.dataset.api?.trim() || DEFAULT_API_BASE[kind]
  const descriptions = parseDescriptions(el.dataset.descriptions)
  const topUrl = parseTopUrl(el.dataset.topUrl)

  /** いま画面に出している受注。プレビューでは作り物が入る。 */
  let order: ViewModel | null = null
  let name = ''
  let description = descriptions[0]
  /** 別注版のダウンロードリンクに入れる guid。受注を引けたら入る（プレビューは作り物）。 */
  let guid: string | null = null

  function view(): ViewContext {
    return { kind, guid, apiBase }
  }

  function viewOf(state: State): string {
    switch (state) {
      case 'issue':
        return order ? issueView({ ...order, name }, { descriptions, description }, view()) : notFoundView()
      case 'reissue':
        return reissueView(view())
      case 'done':
        return doneView(topUrl, view())
      case 'sending':
        return sendingView()
      case 'error':
        return errorView()
      case 'notFound':
        return notFoundView()
    }
  }

  function show(state: State): void {
    el.innerHTML = viewOf(state)
    bind(state)
  }

  /** 描画のたびに張り直す。innerHTML を差し替えているので前の listener は一緒に消えている。 */
  function bind(state: State): void {
    const errorsEl = el.querySelector<HTMLElement>('[data-wu-receipt-field="errors"]')
    const nameEl = el.querySelector<HTMLInputElement>('[data-wu-receipt-field="name"]')
    const descEl = el.querySelector<HTMLSelectElement>('[data-wu-receipt-field="description"]')
    const buttonEl = el.querySelector<HTMLButtonElement>('[data-wu-receipt-action]')

    function refreshErrors(): void {
      if (!errorsEl) return
      const errors = validateName(name)
      errorsEl.innerHTML = errorItemsHtml(errors)
      // 店の CSS に消されないよう inline で出し入れする（CSS より強い）。
      errorsEl.style.display = errors.length > 0 ? '' : 'none'
    }

    if (nameEl) {
      // 初期表示では出さない（宛名は API から入っているのが通常で、いきなり赤字を見せる意味が無い）。
      // 現行は ng-show が常時評価するので空なら初手から出るが、そこは合わせない方が素直。
      nameEl.addEventListener('input', () => {
        name = nameEl.value
        refreshErrors()
      })
    }
    if (descEl) {
      descEl.addEventListener('change', () => {
        description = descEl.value
      })
    }
    if (buttonEl) {
      buttonEl.addEventListener('click', () => {
        // 再送信画面には宛名の入力欄が無い。API から受け取った宛名でそのまま送るのも現行どおり
        // （別注版の再ダウンロード画面にはボタン自体が無い）。
        if (state === 'issue') {
          refreshErrors()
          if (!canIssue(name)) return
        }
        submit()
      })
    }
  }

  // --- 表示確認モード（API を叩かない）------------------------------------
  const preview = readPreview(location.search)

  function previewBar(state: State): string {
    return `<div class="wu-receipt-preview-bar">表示確認: ${PREVIEW_LABEL[state]}（この画面では送信されません）</div>`
  }

  function submit(): void {
    if (preview) {
      // ★プレビューでは通信しない。状態の移り変わりだけを見せる。
      show('sending')
      el.insertAdjacentHTML('afterbegin', previewBar('sending'))
      window.setTimeout(() => {
        show('done')
        el.insertAdjacentHTML('afterbegin', previewBar('done'))
      }, PREVIEW_SENDING_MS)
      return
    }

    if (guid === null || order === null) {
      show('notFound')
      return
    }

    show('sending')
    issueReceipt(apiBase, guid, name, description, kind).then(
      (result) => show(result === 'issued' ? 'done' : result === 'notFound' ? 'notFound' : 'error'),
      (e) => {
        console.error(`[wu] ${kind === 'est' ? 'est-' : ''}receipt-issue: 発行に失敗しました`, e)
        show('error')
      },
    )
  }

  if (preview) {
    order = PREVIEW_ORDER_INFO
    name = PREVIEW_ORDER_INFO.name
    guid = PREVIEW_GUID

    if (preview.kind === 'all') {
      // 現行の mode -1（全表示）にあたる。押しても通信しないので、そのまま並べてよい。
      el.innerHTML = PREVIEW_ORDER.map((s) => previewBar(s) + viewOf(s)).join('')
      return
    }
    show(preview.state)
    el.insertAdjacentHTML('afterbegin', previewBar(preview.state))
    return
  }

  // --- 通常動作 -----------------------------------------------------------
  const queryGuid = readGuid(location.search)
  if (queryGuid === null) {
    // 現行はここで画面が真っ白になる（mode -2）。何をすればよいか出す。
    show('notFound')
    return
  }

  // 受注を引くまでは何も描かない（現行も同じ。応答は速く、空の枠を一瞬見せる方が落ち着かない）。
  fetchOrderInfo(apiBase, queryGuid, kind).then(
    (info: OrderInfo | null) => {
      if (info === null) {
        // 受注が無い。現行はここで例外になって真っ白のまま止まる。
        show('notFound')
        return
      }
      order = {
        siteName: info.siteName,
        orderNo: info.orderNo,
        name: info.name,
        price: info.price,
        date: info.date,
        mailAddress: info.mailAddress,
      }
      name = info.name ?? ''
      // ダウンロードリンクに入れるのは URL の guid（応答の guid と同じ値のはずだが、URL の方が確実に手元にある）
      guid = queryGuid
      show(info.isIssued ? 'reissue' : 'issue')
    },
    (e) => {
      // 通信エラー・500 など。「見つからない」と言い切ると客に誤った案内をするので、エラー扱いにする。
      console.error(`[wu] ${kind === 'est' ? 'est-' : ''}receipt-issue: 受注情報を引けませんでした`, e)
      show('error')
    },
  )
}
